import { useEffect, useState } from "react";
import {
  Snowflake,
  Lightning,
  ArrowClockwise,
  Trophy,
  Calendar,
  Percent,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtMoney } from "../../lib/finance-format";
import { useTheme } from "../../contexts/ThemeContext";

/**
 * Debt strategy simulator — appended above/below the DebtsPanel list.
 * Lets the user pick avalanche/snowball + extra monthly payment, shows:
 *   - Months to freedom
 *   - Total interest paid
 *   - Payoff order
 *   - Savings vs baseline
 *   - Balance-over-time area chart
 */
export default function DebtStrategy({ scope }) {
  const { isDark } = useTheme();
  const [strategy, setStrategy] = useState("avalanche");
  const [extra, setExtra] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extraDraft, setExtraDraft] = useState("0");

  const run = async (s = strategy, x = extra) => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/finance/debt-plan", {
        strategy: s,
        extra_monthly: Number(x || 0),
        scope: scope || "all",
      });
      setData(res);
    } catch (err) {
      toast.error("No se pudo generar plan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run(strategy, extra);
  }, [scope]);

  const applyExtra = () => {
    const n = parseFloat(extraDraft);
    setExtra(Number.isFinite(n) && n >= 0 ? n : 0);
    run(strategy, Number.isFinite(n) && n >= 0 ? n : 0);
  };

  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)";
  const axisColor = isDark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.45)";

  if (data?.empty) {
    return (
      <section className="rounded-lg border border-white/8 bg-black/20 p-6 text-center" data-testid="debt-strategy-empty">
        <Trophy size={26} weight="fill" className="mx-auto mb-2 text-emerald-400/70" />
        <div className="text-sm font-heading font-bold text-white mb-1">
          Sin deudas activas
        </div>
        <div className="text-[11px] font-mono text-white/50">
          Cuando agregues una deuda o tengas balance en tarjeta, verás el plan de pago aquí.
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="debt-strategy-panel"
      className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5"
    >
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-red-400">
            DEBT PAYOFF ENGINE · AVALANCHE / SNOWBALL
          </div>
          <h3 className="text-base md:text-lg font-heading font-bold text-white">
            Estrategia de deudas
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Strategy toggle */}
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-black/40 border border-white/8">
            <button
              data-testid="strategy-avalanche"
              onClick={() => { setStrategy("avalanche"); run("avalanche", extra); }}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-widest font-bold transition ${
                strategy === "avalanche" ? "on-solid bg-red-600 text-white" : "text-white/40 hover:text-white/70"
              }`}
              title="Paga la APR más alta primero"
            >
              <Lightning size={11} weight="fill" /> Avalancha
            </button>
            <button
              data-testid="strategy-snowball"
              onClick={() => { setStrategy("snowball"); run("snowball", extra); }}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-widest font-bold transition ${
                strategy === "snowball" ? "on-solid bg-blue-700 text-white" : "text-white/40 hover:text-white/70"
              }`}
              title="Paga el balance más pequeño primero"
            >
              <Snowflake size={11} weight="fill" /> Bola de nieve
            </button>
          </div>

          {/* Extra monthly */}
          <div className="flex items-center gap-1 rounded-lg bg-black/40 border border-white/8 px-2">
            <span className="text-white/40 text-xs font-mono">+$</span>
            <input
              data-testid="extra-monthly-input"
              type="number"
              value={extraDraft}
              onChange={(e) => setExtraDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyExtra()}
              onBlur={applyExtra}
              placeholder="500"
              className="w-16 py-1.5 bg-transparent outline-none text-sm font-mono text-white tabular-nums text-right"
            />
            <span className="text-[9px] uppercase tracking-widest text-white/40">/mes</span>
          </div>

          <button
            onClick={() => run()}
            disabled={loading}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/8 text-white/60 flex items-center justify-center"
          >
            <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-12 text-center text-xs font-mono text-white/40">
          Calculando...
        </div>
      ) : data ? (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard
              label="LIBRE DE DEUDA"
              value={data.chosen.months_to_freedom ? `${data.chosen.months_to_freedom} meses` : "—"}
              sub={
                data.chosen.months_to_freedom
                  ? yearsMonths(data.chosen.months_to_freedom)
                  : "Tus pagos no cubren el interés"
              }
              accent="#10B981"
              Icon={Calendar}
            />
            <StatCard
              label="INTERÉS TOTAL"
              value={fmtMoney(data.chosen.total_interest)}
              sub={
                data.savings_vs_baseline?.interest_saved
                  ? `Ahorras ${fmtMoney(data.savings_vs_baseline.interest_saved)}`
                  : "vs. sin extra"
              }
              accent="#EF4444"
              Icon={Percent}
            />
            <StatCard
              label="ORDEN DE PAGO"
              value={data.chosen.order?.length ? `${data.chosen.order.length} deuda(s)` : "—"}
              sub={data.chosen.order?.[0] || "—"}
              accent={strategy === "avalanche" ? "#F97316" : "#2563EB"}
              Icon={Trophy}
            />
            <StatCard
              label="EXTRA MENSUAL"
              value={fmtMoney(data.extra_monthly)}
              sub={
                data.savings_vs_baseline?.months_saved
                  ? `${data.savings_vs_baseline.months_saved} meses antes`
                  : "Añade extra para acelerar"
              }
              accent="#FACC15"
              Icon={Lightning}
            />
          </div>

          {/* Avalanche vs Snowball comparison */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <ComparePill
              active={strategy === "avalanche"}
              accent="#EF4444"
              Icon={Lightning}
              title="Avalancha"
              months={data.avalanche.months_to_freedom}
              interest={data.avalanche.total_interest}
            />
            <ComparePill
              active={strategy === "snowball"}
              accent="#2563EB"
              Icon={Snowflake}
              title="Bola de nieve"
              months={data.snowball.months_to_freedom}
              interest={data.snowball.total_interest}
            />
          </div>

          {/* Balance over time */}
          {data.chosen.months?.length > 0 && (
            <div className="rounded-lg border border-white/6 bg-black/30 p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
                Balance restante mes a mes
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data.chosen.months} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="debtFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.45} />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: axisColor }}
                    tickLine={false}
                    axisLine={{ stroke: gridColor }}
                    tickFormatter={(v) => `M${v}`}
                  />
                  <YAxis
                    tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: axisColor }}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                    tickLine={false}
                    axisLine={{ stroke: gridColor }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? "#12131C" : "#FFFFFF",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.16)"}`,
                      borderRadius: 8,
                      fontSize: 11,
                      fontFamily: "JetBrains Mono",
                    }}
                    labelFormatter={(v) => `Mes ${v}`}
                    formatter={(v, k) => [fmtMoney(v), k === "remaining_balance" ? "Restante" : k]}
                  />
                  <Area
                    type="monotone"
                    dataKey="remaining_balance"
                    stroke="#EF4444"
                    strokeWidth={2}
                    fill="url(#debtFill)"
                    name="Restante"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Payoff order list */}
          {data.chosen.order?.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
                Orden de eliminación
              </div>
              <div className="flex flex-wrap gap-2">
                {data.chosen.order.map((name, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/30 border border-white/6 text-[11px] font-mono"
                  >
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-[10px]">
                      {i + 1}
                    </span>
                    <span className="text-white font-heading font-semibold">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function yearsMonths(m) {
  if (!m) return "";
  const y = Math.floor(m / 12);
  const r = m % 12;
  if (y === 0) return `${r}m`;
  if (r === 0) return `${y}a`;
  return `${y}a ${r}m`;
}

function StatCard({ label, value, sub, accent, Icon }) {
  return (
    <div
      className="rounded-lg border border-white/8 bg-[#0F1017] p-3 relative overflow-hidden"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)" }}
    >
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[2px]"
        style={{ backgroundColor: accent, boxShadow: `0 0 8px ${accent}66` }}
      />
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
          {label}
        </div>
        {Icon && <Icon size={11} weight="bold" style={{ color: accent }} className="opacity-60" />}
      </div>
      <div className="text-base font-mono font-bold tabular-nums text-white">{value}</div>
      {sub && <div className="text-[10px] font-mono text-white/50 truncate mt-0.5">{sub}</div>}
    </div>
  );
}

function ComparePill({ active, accent, Icon, title, months, interest }) {
  return (
    <div
      className={`rounded-lg p-3 border transition ${
        active ? "border-white/20" : "border-white/6 opacity-70"
      }`}
      style={{
        background: active ? `${accent}12` : "transparent",
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={13} weight="fill" style={{ color: accent }} />
        <div className="text-sm font-heading font-bold text-white">{title}</div>
        {active && (
          <span className="ml-auto text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: `${accent}33`, color: accent }}>
            SELECCIONADO
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
        <div>
          <div className="text-white/40 uppercase tracking-widest text-[9px]">Meses</div>
          <div className="text-white tabular-nums font-bold text-sm">
            {months ?? "—"}
          </div>
        </div>
        <div>
          <div className="text-white/40 uppercase tracking-widest text-[9px]">Interés total</div>
          <div className="text-white tabular-nums font-bold text-sm">
            {fmtMoney(interest)}
          </div>
        </div>
      </div>
    </div>
  );
}
